import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Button, Breadcrumb, Modal, message, DatePicker, InputNumber, Row, Col, Form, Grid, Card, Statistic, Avatar, Tag, Tooltip, Dropdown, Select, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  RightOutlined,
  TeamOutlined,
  UserOutlined,
  TrophyOutlined,
  CalendarOutlined,
  StarOutlined,
  PhoneOutlined,
  GlobalOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { partenairesService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';
import { toast } from 'react-toastify';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

// Fonction pour générer une couleur à partir du nom
const getAvatarColor = (name) => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
    '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d'
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Formater le téléphone (sans le préfixe +225)
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('225')) cleaned = cleaned.slice(3);
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }
  return cleaned || phone;
};

// Formater la date
const formatDate = (date) => {
  if (!date) return '-';

  // Si c'est déjà au format DD/MM/YYYY, parser avec le bon format en premier
  const formats = [
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
  ];

  let parsed = null;

  // Essayer d'abord les formats spécifiques
  for (const fmt of formats) {
    const attempt = dayjs(date, fmt, true); // true = mode strict
    if (attempt.isValid()) {
      parsed = attempt;
      break;
    }
  }

  // Fallback sur l'auto-detection seulement si aucun format ne matche
  if (!parsed) {
    parsed = dayjs(date);
  }

  if (parsed && parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }

  if (typeof date === 'string') return date;
  return '-';
};

// Formater les montants
const formatAmount = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Couleur selon le niveau de points
const getPointsColor = (points) => {
  if (points >= 3000) return '#52c41a';
  if (points >= 2000) return '#1890ff';
  if (points >= 1000) return '#faad14';
  return '#ff4d4f';
};

const Partenaires = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPartenaires: 0,
    pointsTotal: 0,
    pointsMoyen: 0,
    today: 0
  });

  // Recherche + filtre dates + filtre ville
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [selectedCity, setSelectedCity] = useState(undefined);

  // Pagination/tri serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt');

  const navigate = useNavigate();

  // Edition
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const [isEditModalVisible, setEditModalVisible] = useState(false);

  // Villes
  const [cities, setCities] = useState([]);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const todayStr = dayjs().format('DD/MM/YYYY');

    const todayCount = data.filter(item => {
      if (!item.createdAt) return false;
      // createdAt vient du backend au format "DD/MM/YYYY HH:mm:ss"
      return String(item.createdAt).startsWith(todayStr);
    }).length;

    const totalPoints = data.reduce((sum, item) => sum + Number(item?.soldePoints?.point || 0), 0);
    const avgPoints = data.length > 0 ? totalPoints / data.length : 0;

    setStats({
      totalPartenaires: data.length,
      pointsTotal: totalPoints,
      pointsMoyen: avgPoints,
      today: todayCount
    });
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        // Ne pas envoyer 'page' car la pagination est côté client
        limit: 5000,
        sort,
        q: q?.trim() || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
        city: selectedCity || undefined,
      };
      const data = await partenairesService.getList(params);

      const allData = Array.isArray(data) ? data : (data.items || []);
      setAllItems(allData);
      setTotal(allData.length);
      calculateStats(allData);
    } catch (e) {
      console.error('Erreur chargement partenaires :', e);
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [sort, q, dateRange, selectedCity, calculateStats]);

  // Effet pour le fetch initial et lors des changements de filtres
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Charger les villes actives au montage
  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await citiesService.getAll();
        const allCities = Array.isArray(data) ? data : (data.data || []);
        // Filtrer pour ne garder que les villes actives
        const activeCities = allCities.filter(city => city.isActive === true);
        setCities(activeCities);
      } catch (error) {
        console.error('Erreur chargement villes:', error);
      }
    };
    loadCities();
  }, []);

  // Effet séparé pour la pagination côté client (sans refetch API)
  useEffect(() => {
    const start = (page - 1) * limit;
    const end = start + limit;
    setItems(allItems.slice(start, end));
  }, [page, limit, allItems]);

  // Export
  const exportToExcel = async () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const rows = allItems.map(item => ({
        Nom: item.firstName || '',
        Prenom: item.lastName || '',
        Telephone: formatPhoneNumber(item.phone),
        Ville: item.city?.name || 'Non assignée',
        Date: formatDate(item.createdAt),
        Points: Number(item?.soldePoints?.point ?? 0).toFixed(2)
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Partenaires");
      XLSX.writeFile(workbook, "partenaires.xlsx");
      message.success(`${allItems.length} partenaires exportés`);
    } catch (e) {
      message.error('Export Excel: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  const exportToCSV = async () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const rows = allItems.map(item => ({
        Nom: item.firstName || '',
        Prenom: item.lastName || '',
        Telephone: formatPhoneNumber(item.phone),
        Ville: item.city?.name || 'Non assignée',
        Date: formatDate(item.createdAt),
        Points: Number(item?.soldePoints?.point ?? 0).toFixed(2)
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'partenaires.csv');
      message.success(`${allItems.length} partenaires exportés`);
    } catch (e) {
      message.error('Export CSV: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  const exportToPDF = async () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des partenaires TIGOON", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Nom', 'Prénom', 'Téléphone', 'Ville', 'Date', 'Points']],
        body: allItems.map((item) => [
          item.firstName || '',
          item.lastName || '',
          formatPhoneNumber(item.phone),
          item.city?.name || 'Non assignée',
          formatDate(item.createdAt),
          Number(item?.soldePoints?.point ?? 0).toFixed(2)
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [250, 140, 22] },
      });

      doc.save("partenaires.pdf");
      message.success(`${allItems.length} partenaires exportés`);
    } catch (e) {
      message.error('Export PDF: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  const handleDelete = (partenaire) => {
    const { _id, firstName, lastName, phone } = partenaire;
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();

    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            style={{ backgroundColor: '#ff4d4f' }}
            icon={<DeleteOutlined />}
          />
          <span>Confirmer la suppression</span>
        </div>
      ),
      content: (
        <Card
          size="small"
          style={{ marginTop: 16, background: '#fff1f0', border: '1px solid #ffa39e' }}
        >
          <p style={{ marginBottom: 8 }}><strong>Partenaire :</strong> {fullName}</p>
          <p style={{ marginBottom: 0 }}><strong>Téléphone :</strong> {formatPhoneNumber(phone)}</p>
        </Card>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 480,
      okButtonProps: { danger: true, size: controlSize },
      cancelButtonProps: { size: controlSize },
      onOk: async () => {
        try {
          await partenairesService.supprimer(_id);
          message.success("Partenaire supprimé avec succès !");
          fetchList();
        } catch (error) {
          console.error("Erreur lors de la suppression :", error);
          message.error("Une erreur est survenue pendant la suppression.");
        }
      }
    });
  };

  const columns = [
    {
      title: 'Partenaire',
      key: 'user',
      width: isMobile ? 150 : 220,
      render: (_, record) => {
        const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim();
        const initials = fullName
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar
              size={36}
              style={{
                backgroundColor: getAvatarColor(fullName),
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              {initials || <UserOutlined />}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {fullName || '-'}
              </div>
              {record.phone ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  copyable={{
                    text: record.phone,
                    tooltips: ['Copier le numéro', 'Numéro copié !'],
                  }}
                >
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {formatPhoneNumber(record.phone)}
                </Typography.Text>
              ) : (
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>—</div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'OTP',
      dataIndex: 'resetPasswordOtp',
      key: 'resetPasswordOtp',
      width: 80,
      align: 'center',
      render: (otp) => otp ? (
        <Tag color="purple" style={{ margin: 0 }}>{otp}</Tag>
      ) : '-'
    },
    {
      title: 'Ville',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      align: 'center',
      render: (city) => city?.name ? (
        <Tag color="blue" style={{ margin: 0 }}>
          <GlobalOutlined style={{ marginRight: 4 }} />
          {city.name}
        </Tag>
      ) : (
        <Tag color="default" style={{ margin: 0 }}>Non assignée</Tag>
      )
    },
    {
      title: 'Date d\'inscription',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: true,
      render: (date) => (
        <Tooltip title={formatDate(date)}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
            {formatDate(date)}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Points',
      key: 'points',
      dataIndex: ['soldePoints', 'point'],
      width: 120,
      align: 'center',
      sorter: true,
      render: (pt) => {
        const points = Number(pt ?? 0);
        return (
          <Tooltip title={`${formatAmount(points)} points`}>
            <Tag
              style={{
                margin: 0,
                padding: '4px 12px',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                backgroundColor: getPointsColor(points),
                color: 'white',
                border: 'none'
              }}
            >
              <TrophyOutlined style={{ marginRight: 6 }} />
              {formatAmount(points)}
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <Tooltip title="Voir détails">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/dashboard/partenaire/${record._id}`)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
          <Can perm="partners:update">
            <Tooltip title="Modifier">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  setEditingPartenaire({
                    ...record,
                    currentPoints: Number(record?.soldePoints?.point ?? 0),
                    pointsToAdd: 0,
                  });
                  setEditModalVisible(true);
                }}
                style={{ borderRadius: 6 }}
              />
            </Tooltip>
          </Can>
          <Can perm="partners:delete">
            <Tooltip title="Supprimer">
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleDelete(record)}
                style={{ borderRadius: 6 }}
              />
            </Tooltip>
          </Can>
        </div>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.field) {
      const field = Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';

      if (field === 'createdAt') setSort(`${dir}createdAt`);
      else if (field === 'firstName') setSort(`${dir}firstName`);
      else if (field === 'lastName') setSort(`${dir}lastName`);
      else if (field === 'phone') setSort(`${dir}phone`);
      else if (field === 'soldePoints.point' || field === 'points') setSort(`${dir}points`);
    }
  };

  // Style des lignes alternées
  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #fffbe6;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #fff7e6 !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-blue {
            background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-green {
            background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-purple {
            background: linear-gradient(135deg, #722ed1 0%, #9254de 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title,
          .stat-card-blue .ant-statistic-title,
          .stat-card-green .ant-statistic-title,
          .stat-card-purple .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-orange .ant-statistic-content,
          .stat-card-blue .ant-statistic-content,
          .stat-card-green .ant-statistic-content,
          .stat-card-purple .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Inscription</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Partenaires TIGOON</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><TeamOutlined style={{ marginRight: 6 }} />Total partenaires</span>}
                value={stats.totalPartenaires}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><TrophyOutlined style={{ marginRight: 6 }} />Points totaux</span>}
                value={stats.pointsTotal}
                precision={2}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-green" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><StarOutlined style={{ marginRight: 6 }} />Points moyen</span>}
                value={stats.pointsMoyen}
                precision={2}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-purple" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CalendarOutlined style={{ marginRight: 6 }} />Aujourd'hui</span>}
                value={stats.today}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} md={6}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher par nom, téléphone..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Filtrer par ville"
                value={selectedCity}
                onChange={(value) => { setSelectedCity(value); setPage(1); }}
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
                suffixIcon={<GlobalOutlined style={{ color: '#fa8c16' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    <GlobalOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={7}>
              <RangePicker
                onChange={(range) => { setDateRange(range); setPage(1); }}
                placeholder={['Date début', 'Date fin']}
                format="DD/MM/YYYY"
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${total})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={controlSize}
            scroll={{ x: 800 }}
            rowClassName={rowClassName}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} partenaires
                </span>
              ),
              showLessItems: isMobile,
            }}
          />
        </Card>
      </div>

      {/* Modal édition */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={40}
              style={{ backgroundColor: '#fa8c16' }}
              icon={<EditOutlined />}
            />
            <span>Modifier le partenaire</span>
          </div>
        }
        open={isEditModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={async () => {
          try {
            const payload = {
              firstName: editingPartenaire.firstName?.trim(),
              lastName: editingPartenaire.lastName?.trim(),
              phone: editingPartenaire.phone,
              pointsDelta: Number(editingPartenaire.pointsToAdd || 0),
              city: editingPartenaire.city?._id || editingPartenaire.city || null,
              numeroPermisConduire: editingPartenaire.numeroPermisConduire?.trim() || null,
            };
            await partenairesService.updatePartenaire(editingPartenaire._id, payload);
            message.success("Partenaire modifié avec succès !");
            setEditModalVisible(false);
            fetchList();
          } catch (error) {
            const errorMessage = error.message || 'Une erreur est survenue lors de la connexion.';
            toast.error(errorMessage, {
              position: 'top-right',
              autoClose: 5000,
            });
          }
        }}
        okText="Enregistrer"
        cancelText="Annuler"
        centered
        maskClosable
        destroyOnClose
        width={isMobile ? 360 : 720}
        okButtonProps={{
          size: controlSize,
          style: {
            background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
            border: 'none'
          }
        }}
        cancelButtonProps={{ size: controlSize }}
      >
        <Form layout="vertical" size={controlSize} style={{ marginTop: 16 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Form.Item label="Nom">
                <Input
                  placeholder="Nom"
                  value={editingPartenaire?.firstName}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({ ...prev, firstName: e.target.value }))
                  }
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Prénom">
                <Input
                  placeholder="Prénom"
                  value={editingPartenaire?.lastName}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({ ...prev, lastName: e.target.value }))
                  }
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Numéro de téléphone">
                <Input
                  addonBefore="+225"
                  placeholder="Ex : 09040607"
                  maxLength={10}
                  value={editingPartenaire?.phone?.replace('+225', '') || ''}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      phone: `+225${e.target.value.replace(/\s+/g, '')}`
                    }))
                  }
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Ville">
                <Select
                  placeholder="Sélectionner une ville"
                  value={editingPartenaire?.city?._id || editingPartenaire?.city}
                  onChange={(value) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      city: value
                    }))
                  }
                  style={{ width: '100%', borderRadius: 8 }}
                  allowClear
                >
                  {cities.map(city => (
                    <Select.Option key={city._id} value={city._id}>
                      <GlobalOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                      {city.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Numéro de permis">
                <Input
                  prefix={<IdcardOutlined style={{ color: '#fa8c16' }} />}
                  placeholder="Ex: CI-123456789"
                  value={editingPartenaire?.numeroPermisConduire || ''}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      numeroPermisConduire: e.target.value.toUpperCase()
                    }))
                  }
                  style={{ borderRadius: 8, textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Solde de points (actuel)">
                <InputNumber
                  style={{ width: '100%', borderRadius: 8 }}
                  disabled
                  value={
                    typeof editingPartenaire?.currentPoints === 'number'
                      ? editingPartenaire.currentPoints
                      : Number(editingPartenaire?.soldePoints?.point ?? 0)
                  }
                  formatter={(v) => (v ?? 0).toLocaleString('fr-FR')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Points à ajouter (aujourd'hui)">
                <InputNumber
                  style={{ width: '100%', borderRadius: 8 }}
                  placeholder="Ex : 50 (ajoute) ou -20 (retire)"
                  min={-1e12}
                  max={1e12}
                  step={0.01}
                  value={editingPartenaire?.pointsToAdd}
                  onChange={(v) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      pointsToAdd: typeof v === 'number' ? v : 0
                    }))
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Nouveau solde (estimé)">
                <InputNumber
                  style={{ width: '100%', borderRadius: 8 }}
                  disabled
                  value={(
                    Number(editingPartenaire?.currentPoints ?? 0) +
                    Number(editingPartenaire?.pointsToAdd ?? 0)
                  )}
                  formatter={(v) => (v ?? 0).toLocaleString('fr-FR')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Partenaires;
